import React, { useState } from 'react';
import { MailPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { cn } from '@/lib/utils';
import { SendEmailNotificationDialog } from './send-email-notification-dialog';

export interface SendMailTriggerProps {
    /** Email address of the recipient */
    email: string;
    /** ID of the recipient (customer or lead) */
    recipientId: string;
    /** Type of recipient */
    recipientType: 'customer' | 'lead';
    /** Name of the recipient for display */
    recipientName: string;
    /** Additional CSS classes */
    className?: string;
}

/**
 * Send Mail Trigger component - displays an icon button to send email notifications
 * Only visible to users with NOTIFICATIONS.CREATE permission
 */
export const SendMailTrigger: React.FC<SendMailTriggerProps> = ({
    email,
    recipientId,
    recipientType,
    recipientName,
    className,
}) => {
    const { hasPermission } = usePermissions();
    const [dialogOpen, setDialogOpen] = useState(false);

    // Only show if user has permission to create notifications
    const canSendNotifications = hasPermission(
        ModulePermission.NOTIFICATIONS,
        PermissionAction.CREATE
    );

    if (!canSendNotifications || !email) {
        return null;
    }

    return (
        <>
            <Button
                variant="ghost"
                size="sm"
                className={cn('h-8 w-8 p-0', className)}
                onClick={() => setDialogOpen(true)}
                title="Send email notification"
            >
                <MailPlus className="h-4 w-4" />
            </Button>

            <SendEmailNotificationDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                recipientEmail={email}
                recipientId={recipientId}
                recipientType={recipientType}
                recipientName={recipientName}
            />
        </>
    );
};
